// Parses the `supportingDoc` output of "Post Note - ICD10 Generate Documentation"
// into the rows the Optimize Docs modal renders.
//
// The process asks the model for one entry per ICD-10 code:
//   { code, hpi: [string], planOfCare: { treatment, guidelines, monitoring } }
// but the outer shape depends on the model. OpenAI JSON mode cannot return a
// top-level array, so the list arrives wrapped under whatever key the model
// picks ({ result: [...] }, { codes: [...] }, ...), or keyed by code. Reading
// only `result` turned any other wrapper into one blank group.

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const looksLikeDoc = (value) => isPlainObject(value) && ("hpi" in value || "planOfCare" in value);

const toDocList = (supportingDoc) => {
  let value = supportingDoc;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) return value.filter(isPlainObject);
  if (!isPlainObject(value)) return [];
  if (looksLikeDoc(value)) return [value];

  const wrapped = Object.values(value).find((v) => Array.isArray(v) && v.some(looksLikeDoc));
  if (wrapped) return wrapped.filter(isPlainObject);

  return Object.entries(value)
    .filter(([, v]) => looksLikeDoc(v))
    .map(([key, v]) => ({ ...v, code: v.code ?? key }));
};

const toItems = (value) => {
  if (Array.isArray(value)) return value;
  if (isPlainObject(value)) return Object.values(value);
  return [value];
};

// Only non-empty strings survive, so React never tries to render an object.
const toSuggestions = (value) =>
  toItems(value)
    .filter((item) => typeof item === "string" && item.trim())
    .map((desc) => ({ desc }));

export const parseSupportingDocs = (supportingDoc) =>
  toDocList(supportingDoc)
    .map((doc) => ({
      code: doc.code,
      hpi: toSuggestions(doc.hpi),
      planOfCare: toSuggestions(doc.planOfCare),
    }))
    .filter((doc) => doc.hpi.length > 0 || doc.planOfCare.length > 0);
